import { useState } from 'react'
import { AlertTriangle, CheckCircle, Copy, Link2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { Invoice } from '@/types/invoice'
import ReminderFeesEditor from './ReminderFeesEditor'
import { formatCurrency, formatDate, STATUS_CONFIG } from './invoiceUtils'

interface Props {
  invoice: Invoice
  onOpen: (id: number) => void
  onAssign: () => void
  onFeesChanged: () => void
  onMarkPaid: (ids: number[]) => void
  markPaidPending: boolean
}

/**
 * Beide Richtungen der Mahnungs-Verknuepfung:
 * an einer Rechnung die Mahnungen, an einer Mahnung die zugeordneten Rechnungen.
 */
export default function InvoiceRemindersPanel({
  invoice, onOpen, onAssign, onFeesChanged, onMarkPaid, markPaidPending,
}: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const isReminder = invoice.type === 'reminder'

  /* ── An einer Rechnung: die Mahnungen ── */
  if (!isReminder) {
    if (!invoice.linkedReminders?.length) return null
    return (
      <section className="overflow-hidden rounded-lg border border-orange-200 bg-white">
        <h3 className="flex items-center gap-1.5 border-b border-orange-100 bg-orange-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-orange-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          Mahnungen
          <span className="rounded-full border border-orange-200 bg-white px-1.5 tracking-normal">{invoice.linkedReminders.length}</span>
        </h3>
        {invoice.linkedReminders.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onOpen(r.id)}
            className="flex w-full items-center gap-2.5 border-t border-gray-100 px-3 py-2 text-left transition-colors first:border-t-0 hover:bg-orange-50"
          >
            <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-orange-100 text-[11px] font-bold text-orange-700">
              {r.reminder_level ?? '?'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold text-gray-900">
                Mahnstufe {r.reminder_level ?? '?'}
                {r.linked_count > 1 && (
                  <span className="ml-1.5 font-normal text-amber-700">Sammelmahnung über {r.linked_count} Rechnungen</span>
                )}
              </span>
              <span className="block text-[10px] tabular-nums text-gray-500">
                {formatDate(r.invoice_date)} · {STATUS_CONFIG[r.status]?.label || r.status}
              </span>
            </span>
            <span className="flex-shrink-0 text-right text-xs tabular-nums">
              {formatCurrency(r.gross_amount)}
              <span className="block text-[10px] text-gray-500">
                {r.fees_amount != null ? (
                  <>
                    Nebenford. {formatCurrency(r.fees_amount)}
                    {/* Bei einer Sammelmahnung zaehlt fuer DIESE Rechnung nur der Anteil. */}
                    {r.linked_count > 1 && r.fee_share != null && <> · anteilig {formatCurrency(r.fee_share)}</>}
                  </>
                ) : (
                  <span className="italic text-gray-400">Nebenforderung unbekannt</span>
                )}
              </span>
            </span>
          </button>
        ))}
      </section>
    )
  }

  /* ── An einer Mahnung: die zugeordneten Rechnungen ── */
  const linked = invoice.linkedInvoices || []
  const paid = linked.filter((i) => i.status === 'paid')
  const paidSum = paid.reduce((s, i) => s + (i.gross_amount || 0), 0)
  const remaining = Math.round(((invoice.gross_amount || 0) - paidSum) * 100) / 100

  const chosen = linked.filter((i) => selected.has(i.id))
  const chosenSum = Math.round(chosen.reduce((s, i) => s + (i.gross_amount || 0), 0) * 100) / 100
  const reference = chosen.map((i) => i.invoice_number || i.id).join(',')

  const copy = (text: string, what: string) => {
    navigator.clipboard?.writeText(text)
    toast.success(`${what} kopiert`)
  }

  return (
    <>
      <ReminderFeesEditor invoice={invoice} onChanged={onFeesChanged} />

      <section className="overflow-hidden rounded-lg border border-blue-200 bg-white">
        <h3 className="flex items-center gap-1.5 border-b border-blue-100 bg-blue-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-blue-700">
          <Link2 className="h-3.5 w-3.5" />
          Zugeordnete Rechnungen
          <span className="rounded-full border border-blue-200 bg-white px-1.5 tracking-normal">{linked.length}</span>
          <button
            type="button"
            onClick={onAssign}
            className="ml-auto rounded border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold tracking-normal text-blue-700 transition-colors hover:bg-blue-100"
          >
            Zuordnung verwalten
          </button>
        </h3>

        {linked.length === 0 ? (
          <p className="flex items-center gap-2 px-3 py-2.5 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
            Diese Mahnung ist keiner Rechnung zugeordnet.
          </p>
        ) : (
          <>
            {linked.map((inv) => {
              const isPaid = inv.status === 'paid'
              const isCredit = inv.type === 'credit_note' || (inv.gross_amount ?? 0) < 0
              const isSelected = selected.has(inv.id)
              return (
                <div
                  key={inv.id}
                  className={`flex items-center gap-2.5 border-t border-gray-100 px-3 py-2 first:border-t-0 ${
                    isPaid ? 'bg-gray-50' : isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  {isPaid ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                  ) : (
                    <input
                      type="checkbox"
                      className="h-4 w-4 flex-shrink-0 rounded border-gray-300"
                      checked={isSelected}
                      aria-label={`Rechnung ${inv.invoice_number || inv.id} auswählen`}
                      onChange={(e) => {
                        const next = new Set(selected)
                        if (e.target.checked) next.add(inv.id); else next.delete(inv.id)
                        setSelected(next)
                      }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onOpen(inv.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate font-mono text-xs font-semibold ${isPaid ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {isCredit && <span className="mr-1 font-sans text-emerald-600">GS</span>}
                        {inv.invoice_number || '—'}
                      </span>
                      <span className="block truncate text-[10px] text-gray-500">{inv.supplier_name}</span>
                    </span>
                    <span className={`flex-shrink-0 text-xs tabular-nums ${isPaid ? 'text-gray-400 line-through' : isCredit ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {formatCurrency(inv.gross_amount)}
                    </span>
                    <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CONFIG[inv.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_CONFIG[inv.status]?.label || inv.status}
                    </span>
                  </button>
                </div>
              )
            })}

            {chosen.length > 0 && (
              <div className="space-y-1.5 border-t border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Verwendungszweck</span>
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-mono font-medium text-gray-800">{reference}</span>
                    <button type="button" onClick={() => copy(reference, 'Verwendungszweck')} className="rounded p-0.5 text-gray-400 hover:text-emerald-700" title="Kopieren">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Betrag</span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-mono font-bold tabular-nums text-emerald-700">{formatCurrency(chosenSum)}</span>
                    <button type="button" onClick={() => copy(chosenSum.toFixed(2).replace('.', ','), 'Betrag')} className="rounded p-0.5 text-gray-400 hover:text-emerald-700" title="Kopieren">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-emerald-200 pt-1.5">
                  <span className="text-emerald-700">{chosen.length} Rechnung(en) ausgewählt</span>
                  <button
                    type="button"
                    disabled={markPaidPending}
                    onClick={() => { onMarkPaid(Array.from(selected)); setSelected(new Set()) }}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {markPaidPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                    Als bezahlt markieren
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-blue-100 bg-blue-50/60 px-3 py-1.5 text-[11px] tabular-nums text-gray-600">
              <span>{linked.length} Rechnungen = <strong>{formatCurrency(invoice.invoices_total)}</strong></span>
              <span className="flex flex-wrap gap-x-3">
                {paid.length > 0 && <span className="font-medium text-emerald-600">Rest {formatCurrency(remaining)}</span>}
                <span>Gesamtforderung {formatCurrency(invoice.gross_amount)}</span>
              </span>
            </div>
          </>
        )}
      </section>
    </>
  )
}
